import { z } from 'zod';

/**
 * Mirrors legacy `MessageTemplateNotesDto`. All three note fields are
 * required strings on the legacy side; we keep that contract.
 */
export const messageTemplateNotesSchema = z.object({
  allSuppliersNotes: z.string(),
  supplierNotes: z.string(),
  privateNotes: z.string(),
});
export type MessageTemplateNotes = z.infer<typeof messageTemplateNotesSchema>;

/**
 * Manual operator-initiated send. Mirrors legacy
 * `SendSupplierTourDto` (POST /operations-sender/whatsapp).
 *
 * The pg_advisory_xact_lock key on the legacy side is
 * `manual-whatsapp:{itineraryItemId}:{tourOperatorId}:{messageTemplateId}` —
 * the service implementation honours that key shape for parity.
 */
export const sendSupplierWhatsappInput = z.object({
  itineraryItemId: z.uuid(),
  tourOperatorId: z.uuid(),
  messageTemplateId: z.uuid(),
  delaySendInMinutes: z
    .number()
    .int()
    .min(0)
    .max(24 * 60)
    .optional(),
  notes: messageTemplateNotesSchema.optional(),
});
export type SendSupplierWhatsappInput = z.infer<
  typeof sendSupplierWhatsappInput
>;

export const sendSupplierWhatsappResultSchema = z.object({
  success: z.boolean(),
  messageId: z.uuid(),
});
export type SendSupplierWhatsappResult = z.infer<
  typeof sendSupplierWhatsappResultSchema
>;

/**
 * Acknowledgement magic-link action — mirrors legacy
 * `GET /operations-sender/preview/:id/action/(accept|decline)`.
 * Public (no Clerk auth); recipient lands here from the WhatsApp body.
 */
export const externalMessageAcknowledgeActionValues = [
  'accept',
  'decline',
] as const;
export const externalMessageAcknowledgeActionSchema = z.enum(
  externalMessageAcknowledgeActionValues
);
export type ExternalMessageAcknowledgeAction = z.infer<
  typeof externalMessageAcknowledgeActionSchema
>;

export const acknowledgeExternalMessageInput = z.object({
  id: z.uuid(),
  action: externalMessageAcknowledgeActionSchema,
});
export type AcknowledgeExternalMessageInput = z.infer<
  typeof acknowledgeExternalMessageInput
>;

/**
 * Result of an acknowledgement. Legacy returns rich error objects when the
 * message is missing/expired/already-acknowledged; we surface those via a
 * discriminated union so the customer-facing page can render distinct
 * states without a second round-trip.
 */
export const acknowledgeExternalMessageResultSchema = z.discriminatedUnion(
  'kind',
  [
    z.object({
      kind: z.literal('ok'),
      id: z.uuid(),
      action: externalMessageAcknowledgeActionSchema,
      newStatus: z.enum(['accepted', 'declined']),
      acknowledgedAt: z.string(),
    }),
    z.object({
      kind: z.literal('already-acknowledged'),
      id: z.uuid(),
      currentStatus: z.string(),
    }),
    z.object({
      kind: z.literal('expired'),
      id: z.uuid(),
    }),
    z.object({
      kind: z.literal('not-found'),
    }),
  ]
);
export type AcknowledgeExternalMessageResult = z.infer<
  typeof acknowledgeExternalMessageResultSchema
>;

/**
 * Public itinerary-item preview tied to an external-message ID. Mirrors
 * legacy `GET /operations-sender/preview/:id` which returns the minimal
 * `{id, title, description, attachments}` shape — we don't leak any
 * itinerary / customer context, only what the recipient needs to confirm
 * the booking.
 */
export const externalMessagePreviewInput = z.object({
  id: z.uuid(),
});
export type ExternalMessagePreviewInput = z.infer<
  typeof externalMessagePreviewInput
>;

export const externalMessagePreviewResultSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('ok'),
    messageId: z.uuid(),
    messageStatus: z.string(),
    acknowledgedAt: z.string().nullable(),
    expireAt: z.string().nullable(),
    item: z.object({
      id: z.uuid(),
      title: z.unknown(),
      description: z.unknown(),
      attachments: z.unknown(),
    }),
  }),
  z.object({
    kind: z.literal('not-found'),
  }),
]);
export type ExternalMessagePreviewResult = z.infer<
  typeof externalMessagePreviewResultSchema
>;
